'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useRouter, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal, Plus } from 'lucide-react'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { DataTable } from '@workspace/ui/components/DataTable'
import { Pagination } from '@workspace/ui/components/Pagination'
import { Button } from '@workspace/ui/components/Button'
import { Menu, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { Separator } from '@workspace/ui/components/Separator'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { toast } from '@workspace/ui/components/Sonner'
import { api, type ProjectTemplate } from '@/shared/lib/api'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { Badge } from '@workspace/ui/components/Badge'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { useCan } from '@/shared/lib/authorization'

import { z } from 'zod'
import { DataTableSorting } from '@workspace/ui/components/DataTable.utils'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    regionFilter: z.string().optional(),
    areaFilter: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/templates/')({
    validateSearch: searchSchema,
    component: TemplatesPage,
})

const columnHelper = createColumnHelper<ProjectTemplate>()

function TemplatesPage() {
    const router = useRouter()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const { t } = useTranslation()
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const pageSize = 10

    const searchQuery = searchParams.q || ''
    const currentPage = searchParams.page || 1
    const regionFilter = searchParams.regionFilter ? searchParams.regionFilter.split(',') : []
    const areaFilter = searchParams.areaFilter ? searchParams.areaFilter.split(',') : []

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }), replace: true })
    }

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setRegionFilter = (values: string[]) =>
        updateSearch({ regionFilter: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setAreaFilter = (values: string[]) =>
        updateSearch({ areaFilter: values.length > 0 ? values.join(',') : undefined, page: 1 })

    const sorting: DataTableSorting | null = useMemo(() => {
        if (!searchParams.sortBy || !searchParams.sortDirection) return null
        return {
            sortBy: searchParams.sortBy,
            sortDirection: searchParams.sortDirection as 'asc' | 'desc',
        }
    }, [searchParams.sortBy, searchParams.sortDirection])

    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const debouncedSearch = useDebounce(searchQuery, 300)

    // Fetch project templates using the API
    const { data, isLoading, refetch } = useQuery({
        queryKey: [
            'project-templates',
            currentPage,
            pageSize,
            debouncedSearch,
            regionFilter,
            areaFilter,
            searchParams.sortBy,
            searchParams.sortDirection,
        ],
        queryFn: () =>
            api.projectTemplate.list({
                page: currentPage,
                take: pageSize,
                q: debouncedSearch ? debouncedSearch.trim() : undefined,
                areaIds: areaFilter.length > 0 ? areaFilter.join(',') : undefined,
                regionIds: regionFilter.length > 0 ? regionFilter.join(',') : undefined,
                order: searchParams.sortDirection
                    ? (searchParams.sortDirection.toUpperCase() as 'ASC' | 'DESC')
                    : 'DESC',
            }),
        retry: 1,
    })

    const { data: regions } = useQuery({
        queryKey: ['regions'],
        queryFn: () => api.region.list({ page: 1, take: 100 }),
    })

    const { data: areas } = useQuery({
        queryKey: ['areas'],
        queryFn: () => api.area.list({ page: 1, take: 100 }),
    })

    const regionOptions = useMemo(
        () => regions?.data.map(region => ({ id: region.id, name: region.name })) || [],
        [regions],
    )

    const areaOptions = useMemo(() => {
        const allAreas = areas?.data || []
        if (regionFilter.length === 0) return allAreas.map(area => ({ id: area.id, name: area.name }))
        return allAreas
            .filter(area => regionFilter.includes(area.region?.id))
            .map(area => ({ id: area.id, name: area.name }))
    }, [areas, regionFilter])

    // Auto-prune area filter when region selection changes
    useEffect(() => {
        if (!areas?.data || areaFilter.length === 0) return

        if (regionFilter.length === 0) {
            updateSearch({ areaFilter: undefined })
            return
        }

        const validAreaIds = new Set(
            areas.data.filter((a: any) => regionFilter.includes(a.region?.id)).map((a: any) => a.id),
        )

        const newAreaFilter = areaFilter.filter(id => validAreaIds.has(id))

        if (newAreaFilter.length !== areaFilter.length) {
            updateSearch({
                areaFilter: newAreaFilter.length > 0 ? newAreaFilter.join(',') : undefined,
            })
        }
    }, [regionFilter, areaFilter, areas])

    // Delete template mutation
    const deleteMutation = useMutation({
        mutationFn: async (templateIds: string[]) => {
            const promises = templateIds.map(id => api.projectTemplate.delete(id))
            await Promise.all(promises)
            return templateIds
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('templates.list.deleteSuccess.description'),
            })
            // Refresh the list and clear selection
            setRowSelection({})
            setCurrentPage(1)
        },
        onError: error => {
            toast.error({
                title: t('common.status.error'),
                description: t('templates.list.deleteError.description'),
            })
            console.error('Delete template error:', error)
        },
    })

    const handleKeydown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (currentPage !== 1) setCurrentPage(1)
            else await refetch()
        }
    }

    const handleBulkDelete = () => {
        const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id])
        if (selectedIds.length === 0) return

        confirm({
            title: t('templates.list.bulkDeleteConfirm.title'),
            description: t('templates.list.bulkDeleteConfirm.description', { count: selectedIds.length }),
            action: {
                label: t('common.actions.delete'),
                onClick: async () => {
                    await deleteMutation.mutateAsync(selectedIds)
                },
            },
            cancel: {
                label: t('common.actions.cancel'),
            },
            variant: 'destructive',
        })
    }

    const showActionColumn = useCan('operate', 'system')

    const columns = [
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <MenuTrigger>
                    <Button variant="outline" size="iconSm" className="rounded-full border-foreground w-4 h-4">
                        <MoreHorizontal className="size-3!" />
                    </Button>
                    <MenuPopover>
                        <div className="px-3 pt-1 font-semibold">{t('common.actions.title')}</div>
                        <Separator className="mt-1" />
                        <Menu className="min-w-[120px]">
                            <MenuItem
                                isDisabled={!showActionColumn}
                                onAction={() => router.navigate({ to: `/templates/${row.original.id}/edit` })}
                                className="cursor-pointer"
                            >
                                {t('common.actions.edit')}
                            </MenuItem>
                            <MenuItem
                                isDisabled={!showActionColumn}
                                onAction={() =>
                                    router.navigate({ to: `/templates/new`, search: { id: row.original.id } })
                                }
                                className="cursor-pointer"
                            >
                                {t('common.actions.duplicate')}
                            </MenuItem>
                            <MenuItem
                                isDisabled={!showActionColumn}
                                onAction={() =>
                                    confirm({
                                        title: t('templates.list.deleteConfirm.title'),
                                        description: t('templates.list.deleteConfirm.description'),
                                        action: {
                                            label: t('common.actions.delete'),
                                            onClick: async () => {
                                                await deleteMutation.mutateAsync([row.original.id])
                                            },
                                        },
                                        cancel: {
                                            label: t('common.actions.cancel'),
                                        },
                                        variant: 'destructive',
                                    })
                                }
                                className="cursor-pointer text-destructive"
                            >
                                {t('common.actions.delete')}
                            </MenuItem>
                        </Menu>
                    </MenuPopover>
                </MenuTrigger>
            ),
            size: 80,
            enableSorting: false,
            meta: {
                className: '!max-w-[46px] !min-w-[46px] !w-[46px]',
            },
        }),
        columnHelper.accessor('name', {
            header: t('templates.list.table.name'),
            enableSorting: false,
            size: 300,
            cell: ({ row }) => {
                const template = row.original
                return (
                    <div
                        className="cursor-pointer truncate max-w-full"
                        onClick={() => {
                            router.navigate({ to: `/templates/${template.id}/edit` })
                        }}
                    >
                        {template.name}
                        {template.isDraft && (
                            <Badge className={'text-white border-none px-2 py-[2px] rounded-full bg-gray-500 ml-2'}>
                                {t('common.draft')}
                            </Badge>
                        )}
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'region',
            header: t('templates.list.table.regionArea'),
            size: 200,
            cell: ({ row }) => {
                const { areas, regions, applyAllRegions, applyAllAreas } = row.original
                const displayRegions = applyAllRegions
                    ? t('templates.list.table.all')
                    : regions.map(region => region.name).join(', ') || t('templates.list.table.all')
                const displayAreas = applyAllAreas
                    ? t('templates.list.table.all')
                    : areas.map(area => area.name).join(', ') || t('templates.list.table.all')

                return (
                    <>
                        <div className="text-sm truncate" title={displayRegions}>
                            {displayRegions}
                        </div>
                        <div className="text-sm text-muted-foreground truncate" title={displayAreas}>
                            {displayAreas}
                        </div>
                    </>
                )
            },
        }),
        columnHelper.accessor('createdBy.fullName', {
            header: t('templates.list.table.creator'),
            enableSorting: false,
            size: 180,
            cell: ({ row }) => {
                const user = row.original.createdBy as any
                if (!user) return '-'
                const fullName = user.fullName
                const position = user.position || user?.role?.displayName || ''

                return (
                    <div className="flex flex-col py-1">
                        <span className="text-sm font-medium text-foreground leading-5">{fullName || '-'}</span>
                        <span
                            className="text-xs text-left text-muted-foreground leading-4 whitespace-normal line-clamp-2"
                            title={position}
                        >
                            {position}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.accessor('createdAt', {
            header: t('templates.list.table.createdDate'),
            enableSorting: true,
            cell: ({ getValue }) => {
                const value = getValue()
                return value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'
            },
            size: 160,
        }),
    ]

    const totalResults = data?.meta?.itemCount || 0
    const from = totalResults > 0 ? (currentPage - 1) * pageSize + 1 : 0
    const to = Math.min(currentPage * pageSize, totalResults)
    const pageCount = data?.meta?.pageCount || 0

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-150px)] min-h-[500px] overflow-hidden">
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between max-lg:flex-col max-lg:items-start max-lg:gap-2">
                <div>
                    <h1 className="text-2xl font-bold mb-1">{t('templates.list.title')}</h1>
                </div>
                <div className="flex gap-4 max-lg:w-full">
                    <Button
                        variant="default"
                        size="lg"
                        className="max-lg:w-full"
                        onPress={() => router.navigate({ to: '/templates/new' })}
                        isDisabled={!showActionColumn}
                    >
                        <Plus className="size-5!" />
                        {t('templates.list.createButton')}
                    </Button>
                </div>
            </div>

            <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                {/* Search Input */}
                <div className={'shrink-0 flex items-center gap-4 max-lg:flex-wrap max-lg:gap-2 max-lg:items-stretch'}>
                    <BsSearchField
                        className="h-10 text-sm rounded-lg"
                        containerClassName="relative w-80 max-lg:w-full"
                        placeholder={t('templates.list.searchPlaceholder')}
                        value={searchQuery}
                        onChange={setSearchQuery}
                        autoTrim
                        onKeyDown={handleKeydown}
                    />
                    <FilterDropdown
                        placeholder={t('common.fields.region')}
                        inputPlaceholder={t('common.fields.region')}
                        options={regionOptions}
                        selectedValues={regionFilter}
                        onChange={setRegionFilter}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                    />
                    <FilterDropdown
                        placeholder={t('common.fields.area')}
                        inputPlaceholder={t('common.fields.area')}
                        options={areaOptions}
                        selectedValues={areaFilter}
                        onChange={setAreaFilter}
                        containerClassName="max-lg:flex-1"
                        className="max-lg:w-full"
                        isDisabled={regionFilter.length === 0}
                        disabledTooltip={t('common.filters.requireRegionFirst')}
                    />
                </div>

                {/* Table */}
                <DataTable
                    columns={columns}
                    data={data?.data || []}
                    enableRowSelection
                    rowSelection={rowSelection}
                    enableSorting={true}
                    sorting={sorting}
                    setSorting={setSorting}
                    setRowSelection={setRowSelection}
                    isLoading={isLoading}
                    emptyString={t('common.emptyState')}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                />

                {/* Pagination Info */}
                {totalResults > 0 && (
                    <div className="shrink-0 flex justify-between items-center min-h-9 max-lg:flex-col max-lg:gap-3">
                        <p className="text-sm text-muted-foreground">
                            {t('common.pagination.showing', { from, to, total: totalResults })}
                        </p>

                        {Object.keys(rowSelection).some(id => rowSelection[id]) && (
                            <Button
                                variant="outline"
                                className="ml-2 mr-auto border-destructive text-destructive hover:bg-destructive hover:text-background"
                                onPress={handleBulkDelete}
                                isDisabled={deleteMutation.isPending || !showActionColumn}
                            >
                                {deleteMutation.isPending ? t('common.actions.deleting') : t('common.actions.delete')}
                            </Button>
                        )}

                        {pageCount && pageCount > 1 && (
                            <Pagination
                                pageCount={pageCount}
                                value={currentPage}
                                onChange={setCurrentPage}
                                previousText={t('common.pagination.previous')}
                                nextText={t('common.pagination.next')}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
